/**
 * StudyGroupDo entity type definitions.
 *
 * The entity DECLARATIONS for StudyGroupDo (`sgMeta`, `sgMembers`,
 * `sgFolders`, `sgActivity`) live in the unified `./entities.ts`
 * schema, tagged `authority: 'group'`. This file is now a pure type
 * module — it owns the data-shape contracts (`StudyGroupMeta`,
 * `SGMember`, `SGFolder`, `SGDeckProps`, `SGActivityEntry`,
 * `SGDeckEditPermission`) plus the standalone `SGDeckPropsSchema` Zod
 * validator used by `server/group/authorize.ts` for per-row checks.
 *
 * Consumers that need the SG entity collection at runtime read it from
 * `entitySchema` filtered by `authority === 'group'` — see
 * `framework/group-entity-registry.ts`.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Entity payload types — owner of the truth for each group-scoped entity
// ---------------------------------------------------------------------------

/** Basic group metadata. Only the owner may edit. */
export type StudyGroupMeta = {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  createdBy: string;
  inviteToken: string;
  createdTimeISO: string;
  updatedAtISO?: string;
};

/**
 * Membership row. Owner-only fields (role) gated server-side in
 * StudyGroupDo's push handler. Self-only fields (lastOpenedAtISO,
 * lastReadAtISO) are enforced the same way.
 */
export type SGMember = {
  userId: string;
  role: 'owner' | 'editor' | 'member';
  joinedAtISO: string;
  lastOpenedAtISO?: string;
  lastReadAtISO?: string;
  displayName?: string;
  avatarUrl?: string;
  /**
   * Server-synced opt-out from this group's leaderboard ranking. When
   * `true`, the REST `/leaderboard` endpoint omits this row from
   * `ranking[]` for OTHER viewers; the owner of the row still sees
   * their own true rank with `hiddenSelf: true`. MP keeps accruing in
   * the background — only visibility flips. Distinct from the
   * per-device "hide leaderboard" toggle, which lives in localStorage.
   */
  leaderboardOptOut?: boolean;
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

/**
 * Per-member score row inside the StudyGroupDo (one row per group ×
 * user). Storage key `sgscore\x01{userId}`. Written by the custom
 * `sg-score-delta` handler in `studygroupdo.ts` — NOT the framework
 * record-push pipeline.
 *
 * ### Centi-MP convention (READ FIRST)
 * Every MP field on this row is stored as an integer in **centi-MP**
 * (1 MP = 100 units). The client computes
 * `Math.round(eventPoints * multiplier * 100)` per card and pushes the
 * integer. Server arithmetic stays exact (no float drift); the REST
 * handler + WS ack divide by 100 and round at the display boundary.
 *
 * Why: the "round on aggregate, never per-event" invariant requires
 * the server to never round mid-aggregation. Centi-MP keeps storage in
 * integers and satisfies that invariant without ever holding floats.
 */
export type SGScore = {
  userId: string;
  /** Lifetime total, centi-MP. Drives the `alltime` leaderboard period. */
  totalMP: number;
  /** Running total for the current UTC ISO week, centi-MP. Lazily reset when `weekStartISO` rolls over on the next delta. */
  weekMP: number;
  /** ISO of the current week's Monday 00:00:00.000Z (UTC). Boundary for the lazy `weekMP` reset. */
  weekStartISO: string;
  /** `YYYY-MM` (UTC) → centi-MP bucket. Server GCs to the most recent 7 entries on every write. */
  monthlyMP: Record<string, number>;
  /** Owner-only running total for the user-local "today", centi-MP. Never returned to other members via REST. */
  todayMP: number;
  /** `YYYY-MM-DD` of `todayMP`'s owning user-local day. Boundary for the lazy `todayMP` reset. */
  todayLocalDayISO: string;
  /** Current group streak in days. Bumps max once per session (gated by `lastStudyLocalDayISO`). */
  streak: number;
  /** `YYYY-MM-DD` of the last user-local day with a streak-bumping delta. Anchor for streak gap arithmetic. */
  lastStudyLocalDayISO: string;
  /**
   * ### Study-time buckets (independent Time ranking)
   * Tracked "real activity" study time in integer MILLISECONDS, written
   * by the `timeDeltaMs` piggyback on `sg-score-delta` and by the
   * dedicated `sg-time-delta` wire. The week / today buckets share their
   * reset anchors with the MP siblings (`weekStartISO` /
   * `todayLocalDayISO`), so both metrics roll over atomically — mirror
   * 1:1 by construction, not by copied logic. Time never feeds MP or the
   * streak.
   *
   * All four fields are OPTIONAL because rows written before the Time
   * feature lack them — readers default to 0 (`?? 0`); the handlers
   * backfill zeros on the next write.
   */
  /** Lifetime tracked study time, ms. Drives the `alltime` Time ranking. */
  totalTimeMs?: number;
  /** Tracked time in the current UTC ISO week, ms. Lazily reset with `weekStartISO`. */
  weekTimeMs?: number;
  /** `YYYY-MM` (UTC) → ms bucket. GC'd to the most recent 7 entries alongside `monthlyMP`. */
  monthlyTimeMs?: Record<string, number>;
  /** Owner-only tracked time for the user-local "today", ms. Lazily reset with `todayLocalDayISO`. */
  todayTimeMs?: number;
  updatedAtISO: string;
  deletedTimeISO?: string | null;
};

/**
 * Folder inside the group. Structure mirrors UserDo's `folders` entity
 * one-for-one (minus `language` — group folders are language-agnostic)
 * so scoped DX is symmetrical.
 */
export type SGFolder = {
  id: string;
  name: string;
  color: string;
  archivedTimeISO?: string | null;
  parentFolder?: string | null;
  orderIndex?: number;
  sortKey?: string;
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

/**
 * Who may draw (pencil / shapes) on the group's shared PDF sources.
 *
 *   - "everyone"    → any group member may draw (default when absent —
 *                     drawing on shared material is the collaborative
 *                     feature itself).
 *   - "specific"    → group owner + anyone in `allowedUserIds`.
 *   - "owner-only"  → only the group owner.
 *
 * Enforced in TWO places: the client hides the drawing tools, and
 * StudyGroupDo refuses to apply binary Yjs updates on `hw\x01*` doc
 * keys from users outside the allow set (see `canApplyDocUpdate`).
 * Only the group owner may change these settings.
 */
export type SGDrawPermissionMode = 'everyone' | 'specific' | 'owner-only';

export type SGDrawSettings = {
  mode: SGDrawPermissionMode;
  /** Only meaningful when mode === 'specific'. */
  allowedUserIds: string[];
  updatedAtISO?: string;
};

/** Pure predicate shared by the server gate and the client UI. */
export function canDrawInGroup(
  settings: SGDrawSettings | undefined | null,
  userId: string,
  groupOwnerId: string | null | undefined,
  isMember: boolean,
): boolean {
  if (!isMember) return false;
  if (groupOwnerId && userId === groupOwnerId) return true;
  const mode = settings?.mode ?? 'everyone';
  if (mode === 'everyone') return true;
  if (mode === 'specific') {
    return (settings?.allowedUserIds ?? []).includes(userId);
  }
  return false; // owner-only
}

/**
 * Edit-permission mode on a shared deck. Combined with `addedByUserId`
 * and `editors[]`, decides who may push card mutations.
 *
 *   - "everyone"    → any group member may edit.
 *   - "specific"    → deck owner + anyone listed in `editors[]` may edit.
 *   - "owner-only"  → only the deck's `addedByUserId` may edit.
 *
 * Absent field is treated as `"owner-only"` by `canEditSGDeck` so any
 * pre-existing row (before the collaboration refactor) stays locked
 * down until the owner explicitly opens it up.
 */
export type SGDeckEditPermission = 'everyone' | 'specific' | 'owner-only';

export type SGDeckProps = {
  id: string;
  deckName: string;
  addedByUserId: string;
  editors: string[];
  editPermission?: SGDeckEditPermission;
  folderId: string | null;
  sortKey?: string;
  hideSource: boolean;
  sharedAtISO: string;
  lastPushedAtISO?: string;
  displayName?: string;
  color?: string;
  imageBackground?: string;
  thumbnailText?: string;
  languageSettings?: {
    front: string | undefined;
    back: string | undefined;
  };
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

/**
 * Activity feed entry. Ring-buffered; StudyGroupDo trims to the last
 * N entries (N=500) on each append and on a daily alarm.
 */
export type SGActivityEntry = {
  id: string;
  type:
    | 'deck_added'
    | 'deck_removed'
    | 'deck_pushed'
    | 'member_joined'
    | 'member_left'
    | 'cards_studied'
    | 'quiz_completed'
    | 'group_updated'
    // Server-emitted leaderboard events. `leader_change` fires from
    // `sg-score-delta` when the weekly #1 changes hands; `userId` is the
    // NEW leader (the panel filter hides own-activity, so the new leader
    // doesn't see a redundant "you ranked #1" toast). `metadata` carries
    // `{ previousLeaderUserId, newLeaderMP }`. `weekly_recap` fires from
    // the `weekly-recap` DO alarm at week rollover with `userId = ''`
    // (sentinel — the panel renders a trophy avatar instead of a member
    // one and skips the own-actor filter). `metadata` carries the closed
    // week's top-3 podium + a per-member `allRanks` map so each viewer
    // can pick the right copy variant (winner / podium / out-of-podium).
    | 'leader_change'
    | 'weekly_recap';
  userId: string;
  metadata?: Record<string, unknown>;
  createdTimeISO: string;
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
};

/**
 * Sentinel `userId` on server-emitted activity entries that have no
 * single actor — currently the weekly leaderboard recap. The panel's
 * own-activity filter checks for `userId === currentUserId`; an empty
 * sentinel never matches a `user_xxx` id, so the entry shows for
 * every member. The renderer also branches on this to draw a trophy
 * avatar instead of looking the actor up in the member cache.
 */
export const SG_SYSTEM_ACTIVITY_USER_ID = '';

/**
 * Metadata shape for a `leader_change` activity. `previousLeaderUserId`
 * is null only for the very first leader of a week (no one to overtake),
 * in which case the score-delta handler skips the emit altogether — so
 * in practice this field is always a real user id.
 */
export type SGLeaderChangeMetadata = {
  previousLeaderUserId: string;
  newLeaderMP: number;
};

/**
 * Per-row entry on the weekly recap podium. `mp` is DISPLAY MP
 * (centi-MP / 100, rounded) so the renderer never has to know about
 * the centi-MP boundary.
 */
export type SGWeeklyRecapPodiumRow = {
  userId: string;
  mp: number;
};

/**
 * Metadata shape for a `weekly_recap` activity. Snapshot of the closed
 * week — week range comes from `weekStartISO` (Monday of the closed
 * week, UTC midnight) + `weekEndISO` (Sunday of the closed week, UTC
 * 23:59:59). `podium` is top-3 by weekMP; `allRanks` maps every member's
 * userId to their dense rank within the closed week so each viewer
 * can pick the right copy variant without re-running the leaderboard
 * math client-side.
 */
export type SGWeeklyRecapMetadata = {
  weekStartISO: string;
  weekEndISO: string;
  podium: SGWeeklyRecapPodiumRow[];
  allRanks: Record<string, number>;
};

// ---------------------------------------------------------------------------
// SGDeckProps Zod schema
//
// Used by `server/group/authorize.ts` for per-row checks. The on-wire
// envelope validator for `sg-deckProperties-push` is auto-built by the
// framework from this schema via the `deckProperties` split-canonical
// entity declaration in `./entities.ts`.
// ---------------------------------------------------------------------------

export const SGDeckPropsSchema: z.ZodType<SGDeckProps> = z
  .object({
    id: z.string(),
    deckName: z.string(),
    addedByUserId: z.string(),
    editors: z.array(z.string()),
    editPermission: z.enum(['everyone', 'specific', 'owner-only']).optional(),
    folderId: z.string().nullable(),
    sortKey: z.string().optional(),
    hideSource: z.boolean(),
    sharedAtISO: z.string(),
    lastPushedAtISO: z.string().optional(),
    displayName: z.string().optional(),
    color: z.string().optional(),
    imageBackground: z.string().optional(),
    thumbnailText: z.string().optional(),
    languageSettings: z
      .object({
        front: z.string().optional(),
        back: z.string().optional(),
      })
      .passthrough()
      .optional(),
    updatedAtISO: z.string().optional(),
    deletedTimeISO: z.string().nullable().optional(),
  })
  .passthrough() as z.ZodType<SGDeckProps>;

/**
 * A deck's mind map as the group sees it — the sharer's `deckMindmaps`
 * entry, published under `key` = `${sgDeckId}\x01${mindmapId}` so members
 * (who have no copy in their own account) can read it. Same payload as
 * `SyncDeckMindmapEntry`, minus the owner's deck path.
 */
export type SGDeckMindmapEntry = {
  key: string;
  mindmap: { title?: string; nodes: unknown[] };
  layout?: unknown;
  graph?: unknown;
  graphStale?: true;
  updatedAtISO: string;
  deletedTimeISO?: string | null;
};
